/**
 * TourOperatorWarning
 *
 * Cautionary beat that sits between StateBreakdown (Courtney quote + market
 * data) and BeforeYouSell (credentials intro). Dark teal #0e2727 to match
 * adjacent sections — the existing border-b on StateBreakdown above and
 * border-t on BeforeYouSell below act as the brackets around this content,
 * so this section deliberately has no internal borders of its own.
 *
 * Format mirrors StateBreakdown/BeforeYouSell: 2-column with vertical
 * divider via md:border-l. Left = kicker + Didot H2 + italic standfirst.
 * Right = wine-country example + the structural fix.
 *
 * Universal content (state-agnostic). Replaces the champagne version that
 * previously sat after WorkingCredentials.
 */
export function TourOperatorWarning() {
    return (
        <section id="tour-operator" className="scroll-mt-20 bg-[#0e2727]">
            <div className="mx-auto max-w-6xl px-6">
                <div className="border-t border-[#F5F5F5]/15 py-10 sm:py-12 lg:py-14">
                    <div className="grid gap-10 md:grid-cols-2 md:gap-0">
                    {/* Left: kicker + heading + standfirst */}
                    <div className="md:pr-10 lg:pr-14">
                        <p className="lux-caps lux-caps-sm text-[#F5F5F5]/55">
                            A caution worth knowing
                        </p>
                        <h2
                            className="font-didot mt-4 text-[#E8E0D8]"
                            style={{ fontSize: 'clamp(1.75rem, 2.6vw, 2.25rem)', lineHeight: 1.1 }}
                        >
                            Don&rsquo;t accidentally become a tour operator
                        </h2>
                        <p
                            className="font-didot mt-6 text-[#E8E0D8]/85 italic"
                            style={{
                                fontSize: 'clamp(1.05rem, 1.4vw, 1.3rem)',
                                lineHeight: 1.5,
                                letterSpacing: '0.005em',
                            }}
                        >
                            Building what feels like a &ldquo;package&rdquo; for a client can quietly
                            cross the line into <em>tour operating</em>
                            <span style={{ fontFamily: 'Georgia, serif' }}> &mdash; </span>
                            a separate regulatory category with its own credentials, surety bonds,
                            and consumer-protection rules.
                        </p>
                    </div>

                    {/* Right: wine-country example + structural fix */}
                    <div className="md:border-l md:border-[#F5F5F5]/15 md:pl-10 lg:pl-14">
                        <p className="leading-[1.75] text-[#E8E0D8]/85 [&_strong]:font-semibold [&_strong]:text-[#E8E0D8]">
                            Say a client asks you to organize a $10,000 wine country trip
                            &mdash; hotels, private tours, car services, restaurant reservations
                            &mdash; bundled into a single price you collect upfront. If that
                            money lands in your business account and you pay each supplier from
                            it, you&rsquo;ve structured a tour package. In most states, that
                            turns you into a tour operator with <strong>dramatically
                            heavier</strong> licensing and liability than a travel agent
                            carries.
                        </p>
                        <p className="mt-5 leading-[1.75] text-[#E8E0D8]/85 [&_strong]:font-semibold [&_strong]:text-[#E8E0D8]">
                            <strong>The fix is structural, not creative.</strong> LuxRally
                            agents never accept aggregated client funds into their own accounts
                            &mdash; every line item runs through the host agency&rsquo;s
                            licensed entity, so you stay clearly on the agent side of the line
                            even when trips get complex. We cover the playbook to keep your
                            group bookings clean in our travel agent training.
                        </p>
                    </div>
                </div>
                </div>
            </div>
        </section>
    );
}
